import { gql } from "__generated__/gql";
import { BrandLayout } from "__generated__/gql/graphql";
import { useQuery } from "utils/apollo/hooks";
import { useAdminOrBrandContext } from "utils/apollo/hooks/adminOrBrand";
import { OnboardingFlowStatus } from "./metadata";

const query = gql(/* GraphQL */ `
  query BrandOnboardingStatus($brandId: UUID!, $includeRetailers: Boolean!) {
    skus(
      filter: { productLine: { brandId: { equalTo: $brandId } } }
      first: 1
    ) {
      totalCount
    }
    brandRetailers(condition: { brandId: $brandId }, first: 1)
      @include(if: $includeRetailers) {
      totalCount
    }
    # Populate the products page splash/table gate cache before navigation.
    # The onboarding status hook does not read this fragment directly.
    ...ProductLinePresence
  }
`);

type Options = {
  brandId: UUID | null | undefined;
  isAdmin?: boolean;
  layout?: BrandLayout | null;
  skip?: boolean;
};

export default function useBrandOnboardingStatus({
  brandId,
  isAdmin,
  layout,
  skip,
}: Options) {
  const [context, isContextReady] = useAdminOrBrandContext(isAdmin);
  const isSkipped = skip || !brandId;
  const isAnalyticsLayout = layout === BrandLayout.ANALYTICS;

  const { data, loading } = useQuery(query, {
    context,
    skip: isSkipped || !isContextReady,
    variables: brandId
      ? { brandId, includeRetailers: !isAnalyticsLayout }
      : undefined,
    fetchPolicy: "cache-and-network",
  });

  const hasStatusData = !!data;
  const isReady = isSkipped || (isContextReady && hasStatusData && !loading);
  const hasProducts = (data?.skus?.totalCount ?? 0) > 0;
  const hasRetailers = (data?.brandRetailers?.totalCount ?? 0) > 0;
  const targetStatus =
    hasProducts && (isAnalyticsLayout || hasRetailers)
      ? OnboardingFlowStatus.COMPLETED
      : OnboardingFlowStatus.ACTIVE;
  const stepStatuses = isAnalyticsLayout
    ? [hasProducts]
    : [hasProducts, hasRetailers];

  return {
    completedCount:
      !isSkipped && hasStatusData
        ? stepStatuses.filter(Boolean).length
        : undefined,
    hasProducts,
    hasRetailers,
    isReady,
    loading: !isSkipped && (!isContextReady || loading),
    targetStatus,
  };
}
